import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { LiaEditSolid } from "react-icons/lia";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin4Line } from "react-icons/ri";
import { RxCross2 } from "react-icons/rx";
import { CiSearch } from "react-icons/ci";
import { FaUserPlus } from "react-icons/fa";

export const AdminManageSpeedTrainingProducts = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newInfo, setNewInfo] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newImage, setNewImage] = useState("");
  const [newWarmupTemplate, setNewWarmupTemplate] = useState("");
  const [newSpeedTemplate, setNewSpeedTemplate] = useState("");
  const [newYoutubeUrl, setNewYoutubeUrl] = useState(""); // ✅ NEW FIELD

  const [speedTemplateOptions, setSpeedTemplateOptions] = useState([]);
  const [warmupTemplateOptions, setWarmupTemplateOptions] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [newProductSource, setNewProductSource] = useState("AMAX");
const [newPartnerName, setNewPartnerName] = useState("");
const [newPartnerInfo, setNewPartnerInfo] = useState("");

const [newPartnerPurchaseUrl, setNewPartnerPurchaseUrl] = useState("");

const [assignModalOpen, setAssignModalOpen] = useState(false);
const [selectedAssignProduct, setSelectedAssignProduct] = useState(null);
const [athleteEmail, setAthleteEmail] = useState("");

const [isAssigning, setIsAssigning] = useState(false);


  useEffect(() => {
    if (staff?.token) fetchProducts();
  }, [staff?.token, currentPage]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(API_URL.ADMIN_MANAGE_SPEED_PRODUCT, {
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      const data = await res.json();
      setProducts(data.data || []);
      setTotalPages(Math.ceil((data.data || []).length / itemsPerPage));
    } catch {
      setError("Failed to load products.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const [speedRes, warmupRes] = await Promise.all([
          fetch(API_URL.ADMIN_GET_SPEED_TRAINING_TEMPLATE_LIST, {
            headers: { Authorization: `Bearer ${staff.token}` },
          }),
          fetch(API_URL.ADMIN_GET_SPEED_TEMPLATE_WARMUP_ROUTINE_LIST, {
            headers: { Authorization: `Bearer ${staff.token}` },
          }),
        ]);

        const speedData = await speedRes.json();
        const warmupData = await warmupRes.json();

        if (speedRes.ok) {
          setSpeedTemplateOptions(
            speedData.data.map((t) => ({ label: t.title, value: t._id })),
          );
        }
        if (warmupRes.ok) {
          setWarmupTemplateOptions(
            warmupData.data.map((t) => ({
              label: t.template_title,
              value: t._id,
            })),
          );
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };
    fetchTemplates();
  }, [staff?.token]);

  // ✅ CREATE PRODUCT
  const handleCreateProduct = async () => {
    try {
      const formData = new FormData();
      formData.append("title", newTitle);
      formData.append("info", newInfo);
      formData.append("price", newPrice);
      formData.append("youtube_url", newYoutubeUrl); // ✅ Add YouTube URL
      if (newImage) formData.append("image", newImage);
      formData.append("warmup_template_id", newWarmupTemplate || "");
      formData.append("speed_training_template_id", newSpeedTemplate || "");

      formData.append("product_source", newProductSource);
      formData.append("partner_name", newPartnerName);
      formData.append("partner_info", newPartnerInfo);
      formData.append(
        "partner_purchase_url",
        newPartnerPurchaseUrl
      );

      const res = await fetch(API_URL.ADMIN_CREATE_SPEED_PRODUCT, {
        method: "POST",
        headers: { Authorization: `Bearer ${staff.token}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to create product");

      alert("✅ New Speed Training Product Created");

      // ✅ Reset all fields
      setNewTitle("");
      setNewInfo("");
      setNewPrice("");
      setNewImage("");
      setNewYoutubeUrl("");
      setNewWarmupTemplate("");
      setNewSpeedTemplate("");

      setNewProductSource("AMAX");
      setNewPartnerName("");
      setNewPartnerInfo("");
      setNewPartnerPurchaseUrl("");

      // ✅ Close modal and refresh list
      setCreateModalOpen(false);
      fetchProducts();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  // ✅ UPDATE PRODUCT
  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      const formData = new FormData();
      formData.append("title", editingProduct.title || "");
      formData.append("info", editingProduct.info || "");
      formData.append("price", editingProduct.price || "");
      formData.append("youtube_url", editingProduct.youtube_url || ""); // ✅ Include YouTube URL
      formData.append(
        "product_source",
        editingProduct.product_source || "AMAX"
      );
      
      formData.append(
        "partner_name",
        editingProduct.partner_name || ""
      );
      
      formData.append(
        "partner_info",
        editingProduct.partner_info || ""
      );

      formData.append(
        "partner_purchase_url",
        editingProduct.partner_purchase_url || ""
      );


      formData.append(
        "warmup_template_id",
        editingProduct.warmup_template_id || "",
      );
      formData.append(
        "speed_training_template_id",
        editingProduct.speed_training_template_id || "",
      );
      formData.append("status", editingProduct.status || "DRAFT"); // ✅ Added

      if (editingProduct.imageFile instanceof File) {
        formData.append("image", editingProduct.imageFile);
      }

      const response = await fetch(
        `${API_URL.ADMIN_EDIT_SPEED_PRODUCT}${editingProduct._id}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${staff.token}` },
          body: formData,
        },
      );

      if (!response.ok) throw new Error("Failed to update product");
      alert("Product updated successfully");
      setEditModalOpen(false);
      fetchProducts();
    } catch (err) {
      alert("Error updating: " + err.message);
    }
  };

  // ✅ DELETE PRODUCT (unchanged)
  const handleDeleteProduct = async () => {
    try {
      const res = await fetch(
        `${API_URL.ADMIN_DELETE_SPEED_PRODUCT}${selectedProductId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      if (!res.ok) throw new Error("Failed to delete product");
      alert("Product deleted successfully");
      setDeleteModalOpen(false);
      fetchProducts();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const filteredProducts = products.filter((p) =>
    p.title?.toLowerCase().includes(searchQuery),
  );


  const handleAssignAthlete = async () => {
    try {
      setIsAssigning(true);

      if (!athleteEmail) {
        alert("Please enter athlete email");
        return;
      }
  
      if (!selectedAssignProduct?._id) {
        alert("Product not found");
        return;
      }
  
      const response = await fetch(
        API_URL.ADMIN_ASSIGN_PRODUCT_TO_ATHLETE + athleteEmail,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify({
            template_id: selectedAssignProduct._id,
          }),
        }
      );
  
      const json = await response.json();
  
      if (!response.ok) {
        alert(json.message || json.error || "Failed to assign product");
        return;
      }
  
      alert(json.message);
  
      setAthleteEmail("");
      setSelectedAssignProduct(null);
      setAssignModalOpen(false);
  
    } catch (error) {
      console.error("Assign Athlete Error:", error);
      alert("Something went wrong while assigning the product");
    }finally {
    setIsAssigning(false);
  }
  };

  // ✅ UI START
  return (
    <>
      <div className="w-full flex md:h-screen font-inter">
        <StaffSideMenu />
        <div className="max-md:w-full md:flex-1 md:flex md:flex-col md:overflow-hidden md:overflow-y-auto bg-bgBase pb-2">
          {/* --- Top Bar --- */}
          <div className="bg-white w-full pt-5 pb-3 md:py-6 px-5 md:px-10 ">
            <div className="w-full max-w-screen-2xl">
              <div className="flex max-lg:flex-col justify-between lg:items-center gap-5">
                <h1 className="font-inter font-medium text-dark text-xl md:text-2xl xl:text-[28px] !leading-none max-md:pl-12">
                  Products
                </h1>
                <div className="flex max-md:flex-col md:items-center gap-3">
                  <div className="relative w-full min-w-64 max-w-64">
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                      placeholder="Search products..."
                      className="w-full p-2.5 border border-black/10 rounded-lg font-inter font-normal text-sm leading-none pl-10"
                    />
                    <span className="absolute top-1/2 -translate-y-1/2 left-3">
                      <CiSearch color="#666666" size={20} />
                    </span>
                  </div>
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    className="px-6 py-2.5 max-md:w-fit bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                  >
                    Create Product
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="py-5 md:py-8 px-5 md:px-10">
            {/* --- Product Table --- */}
            <div className="w-full max-w-screen-2xl">
              {isLoading ? (
                <div className="bg-white py-2 px-10 rounded-md text-dark font-normal">
                  Loading...
                </div>
              ) : (
                  <div className="overflow-auto show-scrollbar max-h-[600px]">
                      <div className="rounded-10 border border-tblBorder w-full">
                        <table className="table-auto table-radius w-full min-w-[680px] md:min-w-[720px]">
                          <thead className="bg-tblHead border-b border-tblBorder text-slate100 font-inter font-medium text-xs">
                            <tr>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Image
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Title
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Source
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Price
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                YouTube
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Assigned Templates
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Status
                              </th>
                              <th className="px-3 md:px-6 py-2 md:py-4 text-start">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#E5E5E5]">
                            {filteredProducts
                              .slice(
                                (currentPage - 1) * itemsPerPage,
                                currentPage * itemsPerPage,
                              )
                              .map((item) => {
                                const warmupLabel =
                                  warmupTemplateOptions.find(
                                    (opt) => opt.value === item.warmup_template_id,
                                  )?.label || "—";
                                const speedLabel =
                                  speedTemplateOptions.find(
                                    (opt) =>
                                      opt.value === item.speed_training_template_id,
                                  )?.label || "—";

                                return (
                                  <tr
                                    key={item._id}
                                    className="bg-white font-inter font-normal text-dark text-sm"
                                  >
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      {item.image ? (
                                        <img
                                          src={API_URL.SERVER_PICTURE_URL + item.image}
                                          alt="product"
                                          className="h-12 w-12 rounded-lg border border-tblBorder object-cover"
                                        />
                                      ) : (
                                        "No Image"
                                      )}
                                    </td>
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      <p className="text-xs md:text-sm font-medium">
                                        {item.title}
                                      </p>
                                    </td>
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      <div className="flex flex-col gap-1">
                                        <span
                                          className={`w-fit px-3 py-1 rounded-md text-xs font-medium ${
                                            item.product_source === "PARTNER"
                                              ? "bg-[#EEF2FF] text-[#4338CA]"
                                              : "bg-[#ECFDF3] text-[#027A48]"
                                          }`}
                                        >
                                          {item.product_source || "AMAX"}
                                        </span>

                                        {item.product_source === "PARTNER" && (
  <div className="flex flex-col gap-1 mt-1">
    {item.partner_name && (
      <span className="text-xs text-slate100">
        {item.partner_name}
      </span>
    )}

    {item.partner_purchase_url && (
      <span className="text-[10px] text-green-600">
        Purchase URL Configured
      </span>
    )}
  </div>
)}
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      ${item.price}
                                    </td>
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      {item.youtube_url ? (
                                        <a
                                          href={item.youtube_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-dark underline p-2 rounded hover:bg-gray-200"
                                        >
                                          View
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </td>

                                    {/* 🧩 Combined Template Info */}
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      <div className="text-xs leading-tight">
                                        <div>
                                          <span>Warmup:</span>{" "}
                                          <span>{warmupLabel}</span>
                                        </div>
                                        <div>
                                          <span>Speed:</span> <span>{speedLabel}</span>
                                        </div>
                                      </div>
                                    </td>

                                    {/* 🏷️ Status */}
                                    <td className="px-3 md:px-6 py-2 md:py-4">
                                      <span
                                        className={`px-4 py-1 rounded-md text-xs font-medium ${
                                          item.status === "PUBLISHED"
                                            ? "bg-[#E8F5E9] text-[#2E7D32]"
                                            : "bg-[#F3F4F6] text-slate100"
                                        }`}
                                      >
                                        {item.status}
                                      </span>
                                    </td>

                                    <td className="px-3 md:px-6 py-2 md:py-4 flex flex-nowrap gap-2">

                                    <button
                                      onClick={() => {
                                        setSelectedAssignProduct(item);
                                        setAthleteEmail("");
                                        setAssignModalOpen(true);
                                      }}
                                      className="p-2 border border-tblBorder rounded-lg hover:bg-gray-200"
                                      title="Assign Athlete"
                                    >
                                      <FaUserPlus color="#666666" size={18} />
                                    </button>

                                      <button
                                        onClick={() => {
                                          setEditingProduct(item);
                                          setEditModalOpen(true);
                                        }}
                                        className="p-2 border border-tblBorder rounded-lg hover:bg-gray-200"
                                      >
                                        <FiEdit2 color="#666666" size={20} />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSelectedProductId(item._id);
                                          setDeleteModalOpen(true);
                                        }}
                                        className="p-2 border border-tblBorder rounded-lg hover:bg-gray-200"
                                      >
                                        <RiDeleteBin4Line color="#D4183D" size={20} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                  </div>
              )}
            </div>
          </div>

          {/* --- CREATE MODAL --- */}
          {createModalOpen && (
            <div className="fixed inset-0 w-full bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center">
              <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
                <div className="bg-white rounded-lg w-full shadow-lg">
                  <div className="relative py-5 pl-5 md:pl-6 pr-14">
                    <h2 className="text-dark text-xl font-medium capitalize">
                      Create Product
                    </h2>
                    <div className="absolute top-4 right-6">
                      <button
                        onClick={() => setCreateModalOpen(false)}
                        className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                      >
                        <RxCross2 color="#4A5565" size={18} />
                      </button>
                    </div>
                  </div>
                  {/* inputs */}
                  <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                    {/* Title */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Title
                      </label>
                      <input
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Title"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* Info */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Info
                      </label>
                      <textarea
                        value={newInfo}
                        onChange={(e) => setNewInfo(e.target.value)}
                        placeholder="Info"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Price
                      </label>
                      <input
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        placeholder="Price"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* ✅ YouTube URL */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        YouTube
                      </label>
                      <input
                        value={newYoutubeUrl}
                        onChange={(e) => setNewYoutubeUrl(e.target.value)}
                        placeholder="YouTube URL (optional)"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* Product Source */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Product Source
                      </label>

                      <select
                        value={newProductSource}
                        onChange={(e) => setNewProductSource(e.target.value)}
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      >
                        <option value="AMAX">AMAX</option>
                        <option value="PARTNER">PARTNER</option>
                      </select>
                    </div>

                    {/* Partner Fields */}
                    {newProductSource === "PARTNER" && (
                      <>
                        {/* Partner Name */}
                        <div>
                          <label className="block text-slate100 font-normal text-sm mb-1">
                            Partner Name
                          </label>

                          <input
                            value={newPartnerName}
                            onChange={(e) => setNewPartnerName(e.target.value)}
                            placeholder="Partner Name"
                            className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                          />
                        </div>

                        {/* Partner Info */}
                        <div>
                          <label className="block text-slate100 font-normal text-sm mb-1">
                            Partner Info (Optional)
                          </label>

                          <textarea
                            value={newPartnerInfo}
                            onChange={(e) => setNewPartnerInfo(e.target.value)}
                            placeholder="Partner Info"
                            className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                            rows={2}
                          />
                        </div>

                        <div>
                        <label className="block text-slate100 font-normal text-sm mb-1">
                          Partner Purchase URL
                        </label>

                        <input
                          value={newPartnerPurchaseUrl}
                          onChange={(e) =>
                            setNewPartnerPurchaseUrl(e.target.value)
                          }
                          placeholder="https://partner-website.com/product"
                          className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                        />
                      </div>

                      </>
                    )}

                    {/* Templates */}
                    <Select
                      options={warmupTemplateOptions}
                      onChange={(s) => setNewWarmupTemplate(s?.value || "")}
                      placeholder="Select Warmup Template (optional)"
                      isClearable
                    />
                    <Select
                      options={speedTemplateOptions}
                      onChange={(s) => setNewSpeedTemplate(s?.value || "")}
                      placeholder="Select Speed Template (optional)"
                      isClearable
                    />

                    {/* Image Upload */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Image
                      </label>
                      <input
                        type="file"
                        onChange={(e) => setNewImage(e.target.files?.[0])}
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setCreateModalOpen(false)}
                      className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateProduct}
                      className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black ml-auto"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* --- DELETE CONFIRMATION MODAL --- */}
          {deleteModalOpen && (
            <div className="fixed inset-0 w-full bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center">
              <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
                <div className="bg-white rounded-lg w-full shadow-lg">
                  <div className="relative py-5 pl-5 md:pl-6 pr-14">
                    <h2 className="text-dark text-xl font-medium capitalize">
                      Delete Product
                    </h2>
                    <div className="absolute top-4 right-6">
                      <button
                        onClick={() => setDeleteModalOpen(false)}
                        className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                      >
                        <RxCross2 color="#4A5565" size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                    <p className="text-sm font-normal text-dark">
                      Are you sure you want to delete this product?
                    </p>
                  </div>
                  <div className="p-5 md:p-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setDeleteModalOpen(false)}
                      className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteProduct}
                      className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* --- EDIT MODAL --- */}
          {editModalOpen && editingProduct && (
            <div className="fixed inset-0 w-full bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center">
              <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
                <div className="bg-white rounded-lg w-full shadow-lg">
                  {/* title */}
                  <div className="relative py-5 pl-6 pr-14">
                    <h2 className="text-dark text-xl font-medium capitalize">
                      Edit Product
                    </h2>
                    <div className="absolute top-4 right-6">
                      <button
                        onClick={() => setEditModalOpen(false)}
                        className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                      >
                        <RxCross2 color="#4A5565" size={18} />
                      </button>
                    </div>
                  </div>
                  {/* inputs */}
                  <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Title
                      </label>
                      <input
                        value={editingProduct.title}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            title: e.target.value,
                          })
                        }
                        placeholder="Title"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Info
                      </label>
                      <textarea
                        value={editingProduct.info}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            info: e.target.value,
                          })
                        }
                        placeholder="Info"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Price
                      </label>
                      <input
                        value={editingProduct.price}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            price: e.target.value,
                          })
                        }
                        placeholder="Price"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* ✅ YouTube URL Field */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Youtube
                      </label>
                      <input
                        value={editingProduct.youtube_url || ""}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            youtube_url: e.target.value,
                          })
                        }
                        placeholder="YouTube URL"
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      />
                    </div>

                    {/* Product Source */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Product Source
                      </label>

                      <select
                        value={editingProduct.product_source || "AMAX"}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            product_source: e.target.value,
                          })
                        }
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      >
                        <option value="AMAX">AMAX</option>
                        <option value="PARTNER">PARTNER</option>
                      </select>
                    </div>

                    {/* Partner Fields */}
                    {editingProduct.product_source === "PARTNER" && (
                      <>
                        {/* Partner Name */}
                        <div>
                          <label className="block text-slate100 font-normal text-sm mb-1">
                            Partner Name
                          </label>

                          <input
                            value={editingProduct.partner_name || ""}
                            onChange={(e) =>
                              setEditingProduct({
                                ...editingProduct,
                                partner_name: e.target.value,
                              })
                            }
                            placeholder="Partner Name"
                            className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                          />
                        </div>

                        {/* Partner Info */}
                        <div>
                          <label className="block text-slate100 font-normal text-sm mb-1">
                            Partner Info (Optional)
                          </label>

                          <textarea
                            value={editingProduct.partner_info || ""}
                            onChange={(e) =>
                              setEditingProduct({
                                ...editingProduct,
                                partner_info: e.target.value,
                              })
                            }
                            placeholder="Partner Info"
                            rows={2}
                            className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-slate100 font-normal text-sm mb-1">
                            Partner Purchase URL
                          </label>

                          <input
                            value={editingProduct.partner_purchase_url || ""}
                            onChange={(e) =>
                              setEditingProduct({
                                ...editingProduct,
                                partner_purchase_url: e.target.value,
                              })
                            }
                            placeholder="https://partner-website.com/product"
                            className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                          />
                        </div>
                      </>
                    )}

                    {/* Image Upload */}
                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Upload Image
                      </label>
                      <div className="flex flex-wrap gap-2 items-end">
                        {editingProduct.imageFile instanceof File ? (
                          <img
                            src={URL.createObjectURL(editingProduct.imageFile)}
                            alt="Preview"
                            className="mt-1 h-12 w-12 object-contain rounded-md border border-tblBorder"
                          />
                        ) : editingProduct.image ? (
                          <img
                            src={API_URL.SERVER_PICTURE_URL + editingProduct.image}
                            alt="Product"
                            className="mt-1 h-12 w-12 object-contain rounded-md border border-tblBorder"
                          />
                        ) : null}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setEditingProduct((prev) => ({
                                ...prev,
                                imageFile: file, // store new file
                              }));
                            }
                          }}
                          className="w-full text-sm font-medium"
                        />
                      </div>
                    </div>

                    {/* Warmup / Speed Template selectors */}
                    <Select
                      options={warmupTemplateOptions}
                      value={warmupTemplateOptions.find(
                        (opt) => opt.value === editingProduct.warmup_template_id,
                      )}
                      onChange={(s) =>
                        setEditingProduct({
                          ...editingProduct,
                          warmup_template_id: s?.value || "",
                        })
                      }
                      placeholder="Select Warmup Template (optional)"
                      isClearable
                    />
                    <Select
                      options={speedTemplateOptions}
                      value={speedTemplateOptions.find(
                        (opt) =>
                          opt.value === editingProduct.speed_training_template_id,
                      )}
                      onChange={(s) =>
                        setEditingProduct({
                          ...editingProduct,
                          speed_training_template_id: s?.value || "",
                        })
                      }
                      placeholder="Select Speed Template (optional)"
                      isClearable
                    />

                    <div>
                      <label className="block text-slate100 font-normal text-sm mb-1">
                        Status
                      </label>
                      <select
                        value={editingProduct.status || "DRAFT"}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            status: e.target.value,
                          })
                        }
                        className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      >
                        <option value="DRAFT">DRAFT</option>
                        <option value="PUBLISHED">PUBLISHED</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setEditModalOpen(false)}
                      className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateProduct}
                      className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black ml-auto"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* --- Assign Athlete MODAL --- */}
          {assignModalOpen && (
            <div className="fixed inset-0 w-full bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center">
              <div className="w-full max-w-lg">
                <div className="bg-white rounded-lg shadow-lg">

                  <div className="relative py-5 px-6">
                    <h2 className="text-dark text-xl font-medium">
                      Assign Athlete
                    </h2>

                    <button
                      onClick={() => setAssignModalOpen(false)}
                      className="absolute top-4 right-6 p-2 rounded hover:bg-gray-200"
                    >
                      <RxCross2 color="#4A5565" size={18} />
                    </button>
                  </div>

                  <div className="p-6 border-y border-tblBorder">

                    {selectedAssignProduct && (
                      <div className="mb-4">
                        <p className="text-sm text-slate100">
                          Product
                        </p>

                        <p className="font-medium">
                          {selectedAssignProduct.title}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm mb-1">
                        Athlete Email
                      </label>

                      <input
                        type="email"
                        value={athleteEmail}
                        onChange={(e) => setAthleteEmail(e.target.value)}
                        placeholder="athlete@email.com"
                        className="w-full border border-tblBorder rounded-md px-3 py-2.5"
                      />
                    </div>
                  </div>

                  <div className="p-6 flex justify-end gap-3">
                    <button
                      onClick={() => setAssignModalOpen(false)}
                      className="px-5 py-2.5 border border-tblBorder rounded-lg"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleAssignAthlete}
                      disabled={isAssigning}
                      className={`px-5 py-2.5 rounded-lg ${
                        isAssigning
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-dark text-white hover:bg-black"
                      }`}
                    >
                      {isAssigning ? "Assigning..." : "Send"}
                    </button>
                    {isAssigning && (
                      <p className="text-sm text-slate100 mt-2">
                        Assigning product to athlete and sending email...
                      </p>
                    )}
                  </div>

                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
